import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/stores/auth.store'
import { businessService } from '@/services/business.service'
import { visitService } from '@/services/visit.service'
import { useNavigate } from 'react-router-dom'
import logo from '@/assets/logo.svg'
import { LanguageSwitcher } from '@/components/LanguageSwitcher'

export function MyBusinessesPage() {
  const { t } = useTranslation()
  const { token } = useAuthStore()
  const navigate = useNavigate()

  const { data, isLoading } = useQuery({
    queryKey: ['my-businesses'],
    queryFn: () => businessService.getMyBusinesses(token!),
    enabled: !!token,
  })

  const { data: visitsData } = useQuery({
    queryKey: ['my-visits'],
    queryFn: () => visitService.list(token!),
    enabled: !!token,
  })

  const allBusinesses = (data as any)?.businesses || []
  const completedVisits = ((visitsData as any)?.visits || []).filter((v: any) => v.isCompleted)
  const visitedBusinessIds = new Set(completedVisits.map((v: any) => v.businessId))
  const businesses = allBusinesses.filter((b: any) => !visitedBusinessIds.has(b.id))

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <a href="https://u-like-it.de" target="_blank" rel="noopener noreferrer">
              <img src={logo} alt="U-Like-It" className="h-10 w-auto hover:opacity-80 transition-opacity" />
            </a>
            <h1 className="text-3xl font-bold text-gray-900">{t('scout.myBusinesses')}</h1>
          </div>
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/scout/dashboard')}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              {t('scout.dashboard')}
            </button>
            <LanguageSwitcher />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {isLoading ? (
          <div className="text-center py-12 text-gray-500">{t('common.loading')}</div>
        ) : businesses.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <p className="text-gray-500 mb-4">{t('scout.noBusinesses')}</p>
            <button
              onClick={() => navigate('/scout/businesses')}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              {t('scout.findMore')}
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">{businesses.length} {t('scout.reservedBusinesses')}</p>
            {businesses.map((business: any) => (
              <div key={business.id} className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900">{business.name}</h3>
                    <p className="text-sm text-gray-600">{business.address}</p>
                    <p className="text-sm text-gray-500">
                      {business.city}, {business.postalCode}
                    </p>
                    {business.category && (
                      <span className="inline-block mt-2 px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded">
                        {business.category}
                      </span>
                    )}
                    <p className="text-xs text-gray-500 mt-2">
                      {t('scout.reservedAt')}: {business.reservedAt ? new Date(business.reservedAt).toLocaleDateString() : '-'}
                    </p>
                  </div>
                  <button
                    onClick={() => navigate(`/scout/businesses/${business.id}/visit`)}
                    className="ml-4 px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700"
                  >
                    {t('scout.startVisit')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
